import { useToast } from "@chakra-ui/react";
import { PaymentMethod } from "common";
import env from "env";
import { isFileValid, shouldPoll, isInfectedFileError } from "utils/statusHelpers";
import { cloneDeep } from "lodash";
import { DisbursementDataType, PaymentKey } from "pages/Payment";
import {
  getVerifyFileName,
  validationFileSize,
  verifyUploadBeforeTenAMToday,
} from "pages/Payment/Payment.data";
import { resetGetDocumentPrefundInterval } from "pages/Payment/Payment.helper";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileDetailsRequest } from "redux/services/dataLoader/types";
import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentMutation,
} from "redux/services/documentRepository";
import { CreateDocumentResponse } from "redux/services/documentRepository/types";
import {
  useGetOrganizationQuery,
  useGetProfileQuery,
} from "redux/services/membership";
import { getOrganisationMembership, getMimeType } from "utils";
import { useOrgIdWithRel } from "./useOrgIdWithRel";
import { useRoles } from "./useRoles";
import useUploadPresignLink from "./useUploadPresignLink";

interface UseUploadFileProps {
  defaultDisbursementData: DisbursementDataType[];
  paymentProofFiles: any;
  selectedDisbursementDate?: Date;
  intervalPrefundRef: React.MutableRefObject<any>;
  orgId?: string;
}

const useUploadPrefund = ({
  defaultDisbursementData = [],
  paymentProofFiles,
  selectedDisbursementDate,
  intervalPrefundRef,
  orgId,
}: UseUploadFileProps) => {
  const [requestQuery, setRequestQuery] = useState<FileDetailsRequest>({
    requestId: "",
  });

  const requestId = requestQuery.requestId;

  const toast = useToast();

  const { isPSPApproverRole, isPSPMakerRole } = useRoles();
  const {
    orgIdWithRel,
    selectedOrgIdWithRelName,
    selectedOrgIdWithRelCode,
    pspSPC,
    dataGetOrgRel,
  } = useOrgIdWithRel();

  const { data: profileData } = useGetProfileQuery();

  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  const { data: organizationData } = useGetOrganizationQuery(
    {
      orgId: membershipOrganisation?.organisationId || "",
      userId: profileData?.data?.userId || "",
    },
    { skip: !profileData }
  );

  const [
    getPrefundDocument,
    {
      data: prefundDocumentDetail,
      reset: resetPrefundDocumentDetail,
      isLoading: isPrefundDocumentDetailLoading,
      error: prefundDocumentDetailError,
    },
  ] = useGetDocumentMutation();

  const [
    uploadProofFile,
    {
      isSuccess: isUploadProofSuccess,
      data: uploadProofFileData,
      reset: resetUploadProofFile,
      error: uploadProofError,
    },
  ] = useCreateDocumentMutation();

  const [deleteDocument] = useDeleteDocumentMutation();

  const {
    onUploadPresignLink,
    presignError: presignPrefundError,
    setPresignState: setPresignPrefundState,
    isLoading: isLoadingPrefund,
    setIsLoading: setIsLoadingPrefund,
  } = useUploadPresignLink();

  const [disbursementData, setDisbursementData] = useState<
    DisbursementDataType[]
  >(defaultDisbursementData);

  const businessUnitCode =
    selectedOrgIdWithRelCode || organizationData?.data?.leiNumber;

  const tradingName = useMemo(() => {
    if (!isPSPMakerRole) return organizationData?.data?.tradingName ?? "";

    const selectedOrg = dataGetOrgRel?.data?.find(
      (item) => item.ownerOrg.id === orgIdWithRel
    );

    return selectedOrg?.ownerOrg.tradingName ?? "";
  }, [
    dataGetOrgRel?.data,
    isPSPMakerRole,
    orgIdWithRel,
    organizationData?.data?.tradingName,
  ]);

  const proofPrefundFile = useMemo(
    () =>
      disbursementData.find(
        (item) => item.key === PaymentKey.UPLOAD_PROOF_PREFUND
      )?.files?.[0],
    [disbursementData]
  );

  const getVerifyDisbursementDate = (disbursementDate?: Date) => {
    if (disbursementDate) {
      if (verifyUploadBeforeTenAMToday(disbursementDate)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const onCallApiUploadPrefund = (file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const normalOrgId =
        isPSPApproverRole || isPSPMakerRole ? orgIdWithRel : undefined;

      const fileDetails = {
        orgToken: membershipOrganisation?.token,
        userId: profileData?.data?.userId,
        name: file.name,
        contentType: getMimeType(file.name, file.type),
        orgId: orgId ?? normalOrgId,
        meta: {
          contentType: getMimeType(file.name, file.type),
          folderName: env.api.uploadFolderPath,
          keepOriginalFileName: "true",
          preSignedEnabled: "true",
        },
      };

      const response = (await uploadProofFile({
        ...fileDetails,
      })) as {
        data: CreateDocumentResponse;
      };

      if (response?.data?.data?.meta?.preSignedLink) {
        const presignResponse = await onUploadPresignLink(
          response?.data?.data?.meta?.preSignedLink,
          file,
          getMimeType(file.name, file.type),
          PaymentKey.UPLOAD_PROOF_PREFUND
        );

        if (presignResponse && response?.data?.data?.id) {
          getPrefundDocument({
            fetchMetaDataOnly: true,
            documentId: response?.data?.data?.id,
            orgToken: membershipOrganisation?.token,
          });
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const onUploadProof = async (files: File[]) => {
    if (!files?.length) return;

    const serviceProviderCode = !isPSPMakerRole
      ? organizationData?.data?.companyNumber
      : pspSPC;

    const [file] = files;
    const fileName = file.name;
    if (!getVerifyDisbursementDate(selectedDisbursementDate)) return;

    const cloned = cloneDeep(disbursementData);
    const idx = cloned.findIndex(
      (c) => c.key === PaymentKey.UPLOAD_PROOF_PREFUND
    );
    const item = cloned[idx];

    item.files[0] = { fileName };
    const isNameValid = Boolean(
      getVerifyFileName(
        fileName,
        PaymentKey.UPLOAD_PROOF_PREFUND,
        serviceProviderCode,
        businessUnitCode,
        selectedDisbursementDate,
        tradingName
      )
    );
    const isSizeValid = validationFileSize(
      file,
      item.validations.maxFileSize.value
    );

    item.isSuccess = isNameValid && isSizeValid;

    if (!isNameValid) {
      item.files[0].errors = [
        `Uploaded File name is invalid. Correct file name should be PREFUND_GOT_SMBULK_${serviceProviderCode}_${businessUnitCode}_YYYYMMDD.\nPlease reupload with the correct file name.`,
      ];
    } else if (!isSizeValid) {
      item.files[0].errors = [item.validations.maxFileSize.message];
    }

    setDisbursementData(cloned);

    if (item.isSuccess) {
      onCallApiUploadPrefund(file);
    }
  };

  const checkAntiVirusPrefund = useCallback(async () => {
    // APEX-1050: Check for infected file error first (403 with code 031.S01.403.08)
    if (isInfectedFileError(prefundDocumentDetailError)) {
      // Stop polling immediately when infected file detected
      if (intervalPrefundRef.current) {
        clearInterval(intervalPrefundRef.current);
        intervalPrefundRef.current = null;
      }
      setIsLoadingPrefund(false);
      // Error message will be displayed via existing error handling in component
      return;
    }

    if (!isPrefundDocumentDetailLoading && prefundDocumentDetail) {
      const antiVirusStatus = prefundDocumentDetail?.data?.antiVirusStatus;

      // Continue polling if anti-virus is scanning
      if (shouldPoll(antiVirusStatus)) {
        resetGetDocumentPrefundInterval(
          intervalPrefundRef,
          getPrefundDocument,
          prefundDocumentDetail,
          membershipOrganisation?.token
        );
      } else {
        // Both checks complete - stop polling
        clearTimeout(intervalPrefundRef.current);
        setIsLoadingPrefund(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPrefundDocumentDetailLoading,
    prefundDocumentDetail,
    prefundDocumentDetailError,
    intervalPrefundRef,
    getPrefundDocument,
    membershipOrganisation?.token,
  ]);

  const allErrorPrefund = useMemo(
    () => [
      ...((uploadProofError as any)?.data?.errors ?? []),
      ...((prefundDocumentDetailError as any)?.data?.errors ?? []),
      ...(presignPrefundError?.data?.errors ?? []),
    ],
    [uploadProofError, prefundDocumentDetailError, presignPrefundError]
  );

  const errorMessagesPrefund = useMemo(
    () => allErrorPrefund.map((err) => err.message),
    [allErrorPrefund]
  );

  const hasErrorPrefund = errorMessagesPrefund.length > 0;

  const isValidProofPrefundFile = useMemo(
    () => {
      // For NORMAL_PAY (prepaid): proof file is REQUIRED
      if (tradingName === PaymentMethod.NORMAL_PAY) {
        if (!proofPrefundFile) return false;
        
        // Check anti-virus status
        const statusesValid = isFileValid(
          prefundDocumentDetail?.data?.antiVirusStatus
        );

        // Check file-level errors
        const noFileErrors = !hasErrorPrefund;

        return statusesValid && noFileErrors;
      }

      // For non-NORMAL_PAY (postpaid): proof file is OPTIONAL
      if (!proofPrefundFile) {
        return true; // No file needed for postpaid customers
      }

      // If postpaid customer provides a file (optional), validate it
      const statusesValid = isFileValid(
        prefundDocumentDetail?.data?.antiVirusStatus
      );

      const noFileErrors = !hasErrorPrefund;

      return statusesValid && noFileErrors;
    },
    [tradingName, proofPrefundFile, hasErrorPrefund, prefundDocumentDetail]
  );

  const handleRemoveFile = useCallback(
    (key: PaymentKey, intervalRef: { current: any }) => {
      // reset the UI checklist elements
      const newData = disbursementData.map((item) => {
        if (item.key === key) {
          return { ...item, isSuccess: false, files: [] };
        }
        return item;
      });
      setDisbursementData(newData);

      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }

      if (
        key === PaymentKey.UPLOAD_PROOF_PREFUND &&
        uploadProofFileData?.data?.id
      ) {
        deleteDocument({
          documentId: uploadProofFileData?.data?.id,
          orgToken: membershipOrganisation?.token,
        });
        resetUploadProofFile();
        resetPrefundDocumentDetail();
        setPresignPrefundState?.({});
        setIsLoadingPrefund?.(false);
      }
    },
    [
      disbursementData,
      uploadProofFileData?.data?.id,
      deleteDocument,
      membershipOrganisation?.token,
      resetUploadProofFile,
      resetPrefundDocumentDetail,
      setPresignPrefundState,
      setIsLoadingPrefund,
    ]
  );

  useEffect(() => {
    if (paymentProofFiles) {
      setIsLoadingPrefund(true);
      onUploadProof(paymentProofFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProofFiles]);

  useEffect(() => {
    checkAntiVirusPrefund();
  }, [checkAntiVirusPrefund]);

  useEffect(() => {
    // Reset disbursement UI state
    setDisbursementData(defaultDisbursementData);
    setRequestQuery({
      requestId: "",
    });

    // Reset document upload and API states
    resetUploadProofFile();
    resetPrefundDocumentDetail();

    // Reset error and loading states
    setPresignPrefundState({});
    setIsLoadingPrefund(false);

    // Clear any running intervals
    if (intervalPrefundRef.current) clearTimeout(intervalPrefundRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgIdWithRelName]);

  useEffect(() => {
    if (isUploadProofSuccess) {
      toast({
        title: "File Uploaded Successfully",
        description: "",
        status: "success",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadProofSuccess]);

  return {
    proofPrefundFile,
    isLoadingPrefund,
    disbursementData,
    requestId,
    prefundDocumentDetail,
    hasErrorPrefund,
    errorMessagesPrefund,
    uploadProofFileData,
    isValidProofPrefundFile, // Exposed for testing
    isEnableSubmit: !isLoadingPrefund && isValidProofPrefundFile,
    tradingName,
    getVerifyDisbursementDate,
    handleRemoveFile,
    onUploadProof,
  };
};

export default useUploadPrefund;
